import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal, { modalStyles as m } from "./Modal";
import AgeDatePromptModal from "./AgeDatePromptModal";
import ManagerPinModal from "./ManagerPinModal";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { canRegister } from "../permissions";
import { auth } from "../data/api";
import { cutoffSource, verifyScannedId, ID_CARD_TYPE, VERIFY_NON_ID_PERSON } from "../engine/ageVerify";

// 21+ verification — the Android dialog family, routed exactly like processAgeVerification():
//   age_verify "1" AND "2" → DOB + expiry prompt        (showAgeVerificationDialogByBirthDateAndExpire)
//   age_verify "1" OR  "2" → single DOB / expiry prompt (showAgeVerificationDialog)
//   neither               → ID-scan dialog             (showAgeVerificationDialogByScanning)
// The scan dialog's "Manual Verify" opens the single prompt (permission "21"); "Manager Verify" opens
// the manager-PIN keypad when regi_setting "5" is on (else toasts and closes); a manager login or
// permission "CIB" gets a plain "Verify" instead of the scan flow.
// A custom item (CUSTOM_ITEM_CHECK_ID) first asks "Is this product age-restricted?"
// (showAgeRestrictedForCustomItem): Yes → the flow above, No → add it unverified.
//
// onVerified(payload) → cartSlice.setAgeVerified: { idNumber, idCardType, dob, idCardExDate } with the
// same values Android stores (setIdentificationNumber / setSpinnerData / setIdDateOfBirth / setIdExpiryDate).
export default function AgeVerifyModal({ settings, isCustom = false, onVerified, onAddUnverified, onCancel }) {
  const birthday = !!(settings && settings.ageVerifyBirthday);
  const expiry = !!(settings && settings.ageVerifyExpiry);
  const promptMode = birthday && expiry ? "both" : expiry ? "expiry" : birthday ? "dob" : null;
  const [stage, setStage] = useState(isCustom ? "ask" : "route");

  if (stage === "ask") {
    return <CustomItemAgeAsk onYes={() => setStage("route")} onNo={onAddUnverified} />;
  }
  if (stage === "manual") {
    // Manual Verify from the scan dialog: age_verify has neither flag here, so the prompt is DOB.
    return <AgeDatePromptModal mode="dob" onVerify={(d) => onVerified({ idNumber: "", idCardType: ID_CARD_TYPE, ...d })} onCancel={onCancel} />;
  }
  if (stage === "pin") {
    return (
      <ManagerPinModal
        onSuccess={() => onVerified({ idNumber: VERIFY_NON_ID_PERSON, idCardType: VERIFY_NON_ID_PERSON, dob: "", idCardExDate: "" })}
        onClose={() => setStage("route")} // ✕ → back to the scan dialog (Android reopens it)
      />
    );
  }
  if (promptMode) {
    return <AgeDatePromptModal mode={promptMode} onVerify={(d) => onVerified({ idNumber: "", idCardType: ID_CARD_TYPE, ...d })} onCancel={onCancel} />;
  }
  return (
    <AgeScanDialog
      settings={settings}
      onVerified={onVerified}
      onCancel={onCancel}
      onManual={() => setStage("manual")}
      onManagerPin={() => setStage("pin")}
    />
  );
}

// ── showAgeRestrictedForCustomItem ────────────────────────────────────────────────────────────────
function CustomItemAgeAsk({ onYes, onNo }) {
  return (
    <Modal title="Custom Item" onClose={onNo} width={420} zIndex={2350}>
      <div style={{ fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Is this product age-restricted?</div>
      <div style={{ marginTop: 6, fontSize: 14, color: "var(--ink-2)", lineHeight: 1.45 }}>This item can only be sold to customers who meet the legal age requirement.</div>
      <button type="button" style={m.primary} onClick={onYes}>
        Yes, Age-Restricted
      </button>
      <button type="button" style={m.ghost} onClick={onNo}>
        No, Not Age-Restricted
      </button>
    </Modal>
  );
}

// ── showAgeVerificationDialogByScanning ──────────────────────────────────────────────────────────
const MSG = {
  verified: "Verified",
  underage: "Underage – Sale Prohibited",
  expired: "Expired ID",
  unknown: "Sorry! No data found or wrong ID scanned",
};

function AgeScanDialog({ settings, onVerified, onCancel, onManual, onManagerPin }) {
  const now = useMemo(() => new Date(), []);
  const [text, setText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // verifyScannedId() verdict
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // hideAgeVerifyScanningDialogButton: a manager login or the CIB permission bypasses the scan.
  const bypass = String(auth().loginType || "").trim().toLowerCase() === "manager" || canRegister("MENU_CHECK_ID_BYPASS");

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    return () => clearTimeout(timerRef.current);
  }, []);

  // Android: every text change hides the buttons + shows "Scanning…", then (600ms debounce) parses.
  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    clearTimeout(timerRef.current);
    if (!v) return;
    setScanning(true);
    setResult(null);
    timerRef.current = setTimeout(() => {
      const r = verifyScannedId(v, now);
      setScanning(false);
      setResult({ ...r, raw: v });
      if (r.kind !== "success") {
        // Failure / Expired / Unknown clear the field and refocus for a rescan
        setText("");
        if (inputRef.current) inputRef.current.focus();
      }
    }, 600);
  };

  const verifyNonId = () => onVerified({ idNumber: VERIFY_NON_ID_PERSON, idCardType: VERIFY_NON_ID_PERSON, dob: "", idCardExDate: "" });
  const onOk = () => {
    // buttonOk: success → the scanned barcode becomes id_card_no + dob; anything else = Cancel.
    // (Android does not set the card TYPE on this path — idCardType stays "".)
    if (result && result.kind === "success") onVerified({ idNumber: String(result.raw || "").trim(), idCardType: "", dob: result.dobForIDNumber, idCardExDate: "" });
    else onCancel();
  };
  const onManualVerify = () => {
    if (!canRegister("MENU_21_PLUS_PRODUCT_MANUAL_VERIFICATION")) return ToastifyAlert("Access Denied!", "error");
    onManual();
  };
  const onManagerVerify = () => {
    if (settings && settings.managerPin) return onManagerPin();
    ToastifyAlert("Please Checked Manager Pin From Setting", "error");
    onCancel(); // Android dismisses the scan dialog in both branches
  };

  const showResult = !!result && result.kind !== "unknown";
  const showIdle = !scanning && !showResult; // illustration + role buttons (initial + Unknown)
  const okOnly = showResult; // after a verdict only OK remains (Android hides the rest)

  return (
    <Modal title="Verify Customer Age" onClose={onCancel} width={520} zIndex={2350}>
      <div style={sx.sub}>Verify with Driver’s License or ID Card.</div>
      <div style={sx.lead}>
        Confirm the customer is at least 21 years old.
        <br />
        Date of birth must be before <b>{cutoffSource(now)}</b>
      </div>

      {showIdle && (
        <div style={sx.cardWrap} aria-hidden="true">
          <div style={sx.idCard}>
            <div style={sx.idPhoto} />
            <div style={sx.idLines}>
              <span style={{ ...sx.idLine, width: "70%" }} />
              <span style={{ ...sx.idLine, width: "50%" }} />
              <span style={{ ...sx.idLine, width: "60%" }} />
            </div>
            <div style={sx.idBarcode}>
              {Array.from({ length: 26 }).map((_, i) => (
                <span key={i} style={{ ...sx.bar, width: (i * 7) % 3 === 0 ? 3 : 1.5 }} />
              ))}
            </div>
          </div>
          <div style={sx.caption}>Scan above Highlighted Barcode for Verification</div>
          <div style={sx.caption2}>Scan Customer’s Driver’s License or Identification Card</div>
          {result && result.kind === "unknown" && <div style={{ ...sx.verdict, color: "var(--danger, #ef4444)" }}>⚠ {MSG.unknown}</div>}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={m.label}>ID Number</div>
        <input ref={inputRef} style={m.input} value={text} onChange={onChange} autoComplete="off" spellCheck={false} aria-label="ID Number" data-mwskip />
      </div>

      {scanning && (
        <div style={sx.scanning}>
          <span style={sx.spinner} /> Scanning Customers ID....
        </div>
      )}

      {showResult && (
        <div style={sx.resultBox}>
          {result.customerName ? <div style={sx.name}>{result.customerName}</div> : null}
          <div style={sx.kv}>
            <span>Date of Birth:</span>
            <b>{result.dateOfBirth}</b>
            <span style={sx.age}>(Age is {result.age} Years)</span>
          </div>
          {result.kind === "expired" && result.idExpiryDate ? (
            <div style={sx.kv}>
              <span>ID Expiry Date:</span>
              <b>{result.idExpiryDate}</b>
            </div>
          ) : null}
          <div style={{ ...sx.verdict, color: result.kind === "success" ? "var(--ok, #059669)" : "var(--danger, #ef4444)" }}>
            {result.kind === "success" ? "✔ " : "⚠ "}
            {result.kind === "success" ? MSG.verified : result.kind === "expired" ? MSG.expired : MSG.underage}
          </div>
        </div>
      )}

      {okOnly ? (
        <button type="button" style={m.primary} onClick={onOk}>
          OK
        </button>
      ) : showIdle ? (
        bypass ? (
          <div style={sx.row}>
            <button type="button" style={{ ...m.ghost, marginTop: 0, flex: 1 }} onClick={onCancel}>
              Cancel
            </button>
            <button type="button" style={{ ...m.primary, marginTop: 0, flex: 1.4 }} onClick={verifyNonId}>
              Verify
            </button>
          </div>
        ) : (
          <div style={sx.row}>
            <button type="button" style={{ ...m.ghost, marginTop: 0, flex: 1 }} onClick={onCancel}>
              Cancel
            </button>
            <button type="button" style={{ ...m.ghost, marginTop: 0, flex: 1 }} onClick={onManualVerify}>
              Manual Verify
            </button>
            <button type="button" style={{ ...m.primary, marginTop: 0, flex: 1.2 }} onClick={onManagerVerify}>
              Manager Verify
            </button>
          </div>
        )
      ) : null}
      <style>{`@keyframes qv-age-spin{to{transform:rotate(360deg)}}`}</style>
    </Modal>
  );
}

const sx = {
  sub: { fontSize: 13, color: "var(--ink-2)", marginTop: -4 },
  lead: { marginTop: 8, fontSize: 14, color: "var(--ink)", lineHeight: 1.5 },
  cardWrap: { marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  idCard: { width: "min(100%, 300px)", aspectRatio: "1.6", borderRadius: 12, border: "1px solid var(--line)", background: "var(--panel-2)", display: "grid", gridTemplateColumns: "34% 1fr", gridTemplateRows: "1fr auto", gap: 10, padding: 12, boxSizing: "border-box" },
  idPhoto: { borderRadius: 8, background: "var(--line)" },
  idLines: { display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" },
  idLine: { height: 8, borderRadius: 4, background: "var(--line)", display: "block" },
  idBarcode: { gridColumn: "1 / -1", height: 30, borderRadius: 6, border: "2px solid var(--accent)", background: "var(--accent-soft)", display: "flex", alignItems: "stretch", gap: 2, padding: "4px 8px", boxSizing: "border-box" },
  bar: { background: "var(--ink)", display: "block", opacity: 0.85 },
  caption: { fontSize: 13, fontWeight: 700, color: "var(--accent)" },
  caption2: { fontSize: 12, color: "var(--ink-2)" },
  scanning: { marginTop: 10, display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--ink-2)" },
  spinner: { width: 16, height: 16, borderRadius: "50%", border: "2px solid var(--line)", borderTopColor: "var(--accent)", display: "inline-block", animation: "qv-age-spin 0.8s linear infinite" },
  resultBox: { marginTop: 12, padding: 12, borderRadius: 10, border: "1px solid var(--line)", background: "var(--panel-2)", display: "flex", flexDirection: "column", gap: 6 },
  name: { fontWeight: 800, fontSize: 15, color: "var(--ink)" },
  kv: { display: "flex", gap: 8, alignItems: "baseline", fontSize: 14, color: "var(--ink-2)", flexWrap: "wrap" },
  age: { fontSize: 13 },
  verdict: { marginTop: 4, fontSize: 15, fontWeight: 800 },
  row: { display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" },
};
